import { Component, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MessageService, PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmPopup } from 'primeng/confirmpopup';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputText } from 'primeng/inputtext';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, take, tap } from 'rxjs';
import { EditCategoryComponent } from 'src/app/categories/components/edit-category/edit-category.component';
import { EmptyStateComponent } from 'src/app/shared/components/empty-state/empty-state.component';
import { PagePayload } from 'src/app/shared/models/common.model';
import { Page } from 'src/app/shared/models/page';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { CategoriesService } from '../../categories.service';
import { Category } from '../../models';

@Component({
    selector: 'app-list-categories',
    imports: [
        Button,
        ConfirmPopup,
        EmptyStateComponent,
        InputText,
        Paginator,
        PrimeTemplate,
        ReactiveFormsModule,
        TableModule,
        Tooltip,
    ],
    providers: [ConfirmationService, MessageService, DialogService],
    templateUrl: './list-categories.component.html',
    styleUrl: './list-categories.component.scss',
})
export class ListCategoriesComponent implements OnInit {
    loading = false;
    search: FormControl<string> = new FormControl<string>('');
    limit: number = 10;
    offset: number = 0;
    categories: Category[] = [];
    total: number = 0;
    perPage = 10;
    tableMinHeight: string = '0px';
    private readonly categoriesService = inject(CategoriesService);
    private readonly confirmService = inject(ConfirmationService);
    private readonly toasterService = inject(ToasterService);
    private readonly dialogService = inject(DialogService);

    ngOnInit() {
        this.loadCategories();
        this.search.valueChanges
            .pipe(
                distinctUntilChanged(),
                debounceTime(300),
                tap((searchValue: string) => {
                    this.loadCategories(searchValue);
                }),
            )
            .subscribe();
    }

    loadCategories(query?: string) {
        this.loading = true;
        const categoriesPayload: PagePayload = {
            query,
            limit: this.limit,
            offset: this.offset,
        };
        this.categoriesService
            .getCategories(categoriesPayload)
            .pipe(
                take(1),
                tap((customers: Page<Category>) => {
                    this.loading = false;
                    this.categories = customers.result;
                    this.total = customers.total;
                    this.handleTableHeight();
                }),
                catchError(() => {
                    this.loading = false;
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    handleTableHeight(): void {
        if (this.categories.length) {
            this.tableMinHeight = 'auto';
        } else {
            this.tableMinHeight = '50vh';
        }
    }

    onPageChange($event: PaginatorState) {
        this.offset = $event.first || 0;
        this.limit = $event.rows || 10;

        this.loadCategories(this.search.value);
    }

    togglePinCategory(category: Category) {
        const newPinState = !category.isPinned;
        const previousState = category.isPinned;
        category.isPinned = newPinState;

        this.categoriesService
            .updateCategory(category.id, { isPinned: newPinState })
            .pipe(
                take(1),
                catchError(() => {
                    category.isPinned = previousState;
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    createCategory() {
        const dialogRef: DynamicDialogRef = this.dialogService.open(EditCategoryComponent, {
            header: 'انشاء فئة',
            width: '40vw',
            height: '40vh',
            breakpoints: {
                '960px': '75vw',
                '640px': '90vw',
            },
        });
        dialogRef.onClose
            .pipe(
                take(1),
                tap(() => this.loadCategories(this.search.value)),
            )
            .subscribe();
    }

    editCategory(category: any) {
        const dialogRef: DynamicDialogRef = this.dialogService.open(EditCategoryComponent, {
            header: 'تعديل فئة',
            width: '40vw',
            height: '40vh',
            data: {
                categoryName: category.name,
                categoryId: category.id,
            },
            breakpoints: {
                '960px': '75vw',
                '640px': '90vw',
            },
        });
        dialogRef.onClose
            .pipe(
                take(1),
                tap(() => this.loadCategories(this.search.value)),
            )
            .subscribe();
    }

    deleteCategory(category: any, event: Event) {
        this.confirmService.confirm({
            target: event.target as EventTarget,
            message: 'هل أنت متأكد من رغبتك في حذف هذه الفئة؟',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'نعم',
            rejectLabel: 'لا',
            acceptButtonStyleClass: 'p-button-danger p-button-sm',
            rejectButtonStyleClass: 'p-button-text p-button-sm',
            accept: () => {
                this.categoriesService
                    .deleteCategory(category.id)
                    .pipe(
                        take(1),
                        tap(() => {
                            this.toasterService.showSuccessToast('تم حذف الفئة بنجاح');
                            this.loadCategories(this.search.value);
                        }),
                        catchError((e) => {
                            this.toasterService.showErrorToast('فشل حذف الفئة');
                            throw e;
                        }),
                    )
                    .subscribe();
            },
        });
    }
}
